import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { OrgWidgetData } from '../../models/org-widget.models';
import { OrgDialogService } from '../../../org-board/services/org-dialog.service';

interface PolicyGroup {
    statusKey: string;
    status: string;
    count: number;
    icon: string;
    color: string;
}

interface PolicyItem {
    key: string;
    name: string;
    category: string;
    status: string;
    statusKey: string;
    statusIcon: string;
    statusColor: string;
    statusLabel: string;
    description: string;
    version: number;
    effectiveDate: string;
    publishDate: string;
    expiryDate: string;
    searchText: string;
}

@Component({
    selector: 'shared-policy-status-widget',
    imports: [CommonModule, MatIconModule, FormsModule],
    template: `
        <div class="policy-container" *ngIf="data">
            <!-- Total summary -->
            <div class="policy-total">
                <span class="total-value">{{ totalPolicies }}</span>
                <span class="total-label">{{ totalPolicies === 1 ? 'Policy' : 'Policies' }}</span>
                <span class="filtered-note" *ngIf="filteredItems.length !== policyItems.length">
                    ({{ filteredItems.length }} shown)
                </span>
            </div>

            <!-- Status breakdown — clickable toggle filters (multi-select) -->
            <div class="status-grid" *ngIf="statusGroups.length > 0; else emptyState">
                <div
                    class="status-card"
                    *ngFor="let group of statusGroups"
                    [class.active-filter]="isStatusActive(group.statusKey)"
                    (click)="toggleStatusFilter(group.statusKey)"
                >
                    <div class="status-icon-wrap" [style.background]="group.color + '15'">
                        <mat-icon [style.color]="group.color">{{ group.icon }}</mat-icon>
                    </div>
                    <div class="status-info">
                        <span class="status-count">{{ group.count }}</span>
                        <span class="status-label">{{ group.status }}</span>
                    </div>
                    <div class="proportion-bar">
                        <div
                            class="proportion-fill"
                            [style.width.%]="(group.count / totalPolicies) * 100"
                            [style.background]="group.color"
                        ></div>
                    </div>
                </div>
            </div>

            <!-- Search bar -->
            <div class="search-bar">
                <mat-icon class="search-icon">search</mat-icon>
                <input
                    type="text"
                    [(ngModel)]="searchTerm"
                    (ngModelChange)="applyFilters()"
                    placeholder="Search name, category, dates..."
                    class="search-input"
                />
                <button *ngIf="searchTerm || activeStatusFilters.size > 0" class="clear-btn" (click)="clearFilters()">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <!-- Active filter chips (multi-select) -->
            <div class="active-filter-row" *ngIf="activeStatusFilters.size > 0">
                <span class="filter-chip-active" *ngFor="let key of activeFilterKeys" (click)="toggleStatusFilter(key)">
                    <mat-icon [style.color]="getStatusCfg(key).color">
                        {{ getStatusCfg(key).icon }}
                    </mat-icon>
                    {{ getStatusCfg(key).label }}
                    <mat-icon class="chip-close">close</mat-icon>
                </span>
                <span class="filter-chip-clear" *ngIf="activeStatusFilters.size > 1" (click)="clearStatusFilters()">
                    Clear all
                </span>
            </div>

            <!-- Policy list -->
            <div class="policy-list" *ngIf="filteredItems.length > 0; else noResults">
                <div class="list-header">
                    {{ activeStatusFilters.size > 0 ? 'Filtered Policies' : 'All Policies' }}
                </div>
                <div
                    class="policy-item"
                    *ngFor="let p of filteredItems"
                    [class.search-highlight]="searchTerm && isMatch(p)"
                    (click)="onPolicyClick(p)"
                >
                    <mat-icon class="policy-icon" [style.color]="p.statusColor">{{ p.statusIcon }}</mat-icon>
                    <div class="policy-info">
                        <div class="policy-top-row">
                            <span class="policy-name">{{ p.name }}</span>
                            <span class="policy-category" *ngIf="p.category">{{ p.category }}</span>
                        </div>
                        <div
                            class="policy-meta"
                            *ngIf="p.description || p.effectiveDate || p.publishDate || p.expiryDate || p.version > 0"
                        >
                            <span class="meta-item" *ngIf="p.description">{{ p.description }}</span>
                            <span class="meta-date" *ngIf="p.effectiveDate">
                                <mat-icon>event</mat-icon> Effective: {{ p.effectiveDate }}
                            </span>
                            <span class="meta-date" *ngIf="p.publishDate">
                                <mat-icon>publish</mat-icon> Publish: {{ p.publishDate }}
                            </span>
                            <span class="meta-date" *ngIf="p.expiryDate">
                                <mat-icon>event_busy</mat-icon> Expires: {{ p.expiryDate }}
                            </span>
                            <span class="meta-version" *ngIf="p.version > 0">v{{ p.version }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <ng-template #noResults>
                <div class="empty-state" *ngIf="policyItems.length > 0">
                    <mat-icon>search_off</mat-icon>
                    <span>No policies match your filters</span>
                </div>
            </ng-template>

            <ng-template #emptyState>
                <div class="empty-state">
                    <mat-icon>policy</mat-icon>
                    <span>No policies defined</span>
                </div>
            </ng-template>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }

            .policy-container {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            /* Total */
            .policy-total {
                display: flex;
                align-items: baseline;
                gap: 8px;
            }

            .total-value {
                font-size: 32px;
                font-weight: 600;
                color: rgba(0, 0, 0, 0.87);
                line-height: 1;
            }

            .total-label {
                font-size: 14px;
                color: rgba(0, 0, 0, 0.54);
            }

            .filtered-note {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.38);
            }

            /* Status grid */
            .status-grid {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .status-card {
                display: grid;
                grid-template-columns: 32px 1fr;
                grid-template-rows: auto auto;
                gap: 2px 8px;
                padding: 6px 8px;
                border-radius: 6px;
                background: rgba(0, 0, 0, 0.02);
                cursor: pointer;
                transition: all 0.15s;
                border: 1px solid transparent;

                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
                &.active-filter {
                    border-color: rgba(25, 118, 210, 0.4);
                    background: rgba(25, 118, 210, 0.06);
                }
            }

            .status-icon-wrap {
                grid-row: 1 / 3;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            .status-info {
                display: flex;
                align-items: baseline;
                gap: 6px;
            }

            .status-count {
                font-size: 14px;
                font-weight: 600;
                color: rgba(0, 0, 0, 0.87);
            }

            .status-label {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.54);
            }

            .proportion-bar {
                height: 3px;
                border-radius: 2px;
                background: rgba(0, 0, 0, 0.06);
                overflow: hidden;
            }

            .proportion-fill {
                height: 100%;
                border-radius: 2px;
                transition: width 0.4s ease;
            }

            /* Search */
            .search-bar {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 4px 8px;
                border: 1px solid rgba(0, 0, 0, 0.1);
                border-radius: 6px;
            }

            .search-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: rgba(0, 0, 0, 0.38);
                flex-shrink: 0;
            }

            .search-input {
                flex: 1;
                border: none;
                outline: none;
                font-size: 12px;
                background: transparent;
                color: inherit;
                min-width: 0;
            }

            .clear-btn {
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                color: rgba(0, 0, 0, 0.38);
                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            /* Active filter chips */
            .active-filter-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                align-items: center;
            }

            .filter-chip-active {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 2px 6px 2px 4px;
                border-radius: 12px;
                font-size: 11px;
                background: rgba(25, 118, 210, 0.1);
                color: #1976d2;
                cursor: pointer;
                transition: background 0.15s;

                &:hover {
                    background: rgba(25, 118, 210, 0.18);
                }
                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
                .chip-close {
                    font-size: 12px;
                    width: 12px;
                    height: 12px;
                    color: rgba(0, 0, 0, 0.38);
                }
            }

            .filter-chip-clear {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.38);
                cursor: pointer;
                padding: 2px 6px;
                &:hover {
                    color: rgba(0, 0, 0, 0.6);
                }
            }

            /* Policy list */
            .policy-list {
                margin-top: 4px;
                overflow-y: auto;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .list-header {
                font-size: 11px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.54);
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
            }

            .policy-item {
                display: flex;
                align-items: flex-start;
                gap: 6px;
                padding: 5px 4px;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
                &.search-highlight {
                    background: rgba(255, 235, 59, 0.12);
                }
            }

            .policy-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                flex-shrink: 0;
                margin-top: 1px;
            }

            .policy-info {
                flex: 1;
                min-width: 0;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .policy-top-row {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .policy-name {
                flex: 1;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .policy-category {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                background: rgba(0, 0, 0, 0.04);
                padding: 0px 5px;
                border-radius: 8px;
                white-space: nowrap;
                flex-shrink: 0;
            }

            .policy-meta {
                display: flex;
                flex-wrap: wrap;
                gap: 4px 8px;
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
            }

            .meta-item {
                max-width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .meta-date {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                white-space: nowrap;
                mat-icon {
                    font-size: 11px;
                    width: 11px;
                    height: 11px;
                }
            }

            .meta-version {
                background: rgba(0, 0, 0, 0.06);
                padding: 0 4px;
                border-radius: 6px;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 24px;
                color: rgba(0, 0, 0, 0.38);
                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                }
                span {
                    font-size: 13px;
                }
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .total-value {
                    color: rgba(255, 255, 255, 0.87);
                }
                .total-label {
                    color: rgba(255, 255, 255, 0.54);
                }
                .filtered-note {
                    color: rgba(255, 255, 255, 0.38);
                }
                .status-card {
                    background: rgba(255, 255, 255, 0.04);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                    &.active-filter {
                        border-color: rgba(100, 181, 246, 0.3);
                        background: rgba(100, 181, 246, 0.08);
                    }
                }
                .status-count {
                    color: rgba(255, 255, 255, 0.87);
                }
                .status-label {
                    color: rgba(255, 255, 255, 0.54);
                }
                .proportion-bar {
                    background: rgba(255, 255, 255, 0.08);
                }
                .search-bar {
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .search-icon {
                    color: rgba(255, 255, 255, 0.38);
                }
                .search-input {
                    color: rgba(255, 255, 255, 0.87);
                }
                .clear-btn {
                    color: rgba(255, 255, 255, 0.38);
                }
                .filter-chip-active {
                    background: rgba(100, 181, 246, 0.12);
                    color: #64b5f6;
                    .chip-close {
                        color: rgba(255, 255, 255, 0.38);
                    }
                }
                .filter-chip-clear {
                    color: rgba(255, 255, 255, 0.38);
                    &:hover {
                        color: rgba(255, 255, 255, 0.6);
                    }
                }
                .list-header {
                    color: rgba(255, 255, 255, 0.54);
                }
                .policy-item {
                    &:hover {
                        background: rgba(255, 255, 255, 0.06);
                    }
                    &.search-highlight {
                        background: rgba(255, 235, 59, 0.08);
                    }
                }
                .policy-name {
                    color: rgba(255, 255, 255, 0.87);
                }
                .policy-category {
                    color: rgba(255, 255, 255, 0.38);
                    background: rgba(255, 255, 255, 0.06);
                }
                .policy-meta {
                    color: rgba(255, 255, 255, 0.38);
                }
                .meta-version {
                    background: rgba(255, 255, 255, 0.08);
                }
                .empty-state {
                    color: rgba(255, 255, 255, 0.38);
                }
                .policy-list {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        `,
    ],
})
export class PolicyStatusWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;

    public totalPolicies = 0;
    public statusGroups: PolicyGroup[] = [];
    public policyItems: PolicyItem[] = [];
    public filteredItems: PolicyItem[] = [];
    public searchTerm = '';
    public activeStatusFilters = new Set<string>();
    public activeFilterKeys: string[] = [];

    constructor(private dialogService: OrgDialogService) {}

    private statusConfig: Record<string, { icon: string; color: string; label: string }> = {
        draft: { label: 'Draft', icon: 'edit', color: '#78909c' },
        pending_approval: { label: 'Pending Approval', icon: 'hourglass_empty', color: '#f57c00' },
        approved: { label: 'Approved', icon: 'check_circle', color: '#43a047' },
        scheduled: { label: 'Scheduled', icon: 'schedule', color: '#1976d2' },
        published: { label: 'Published', icon: 'publish', color: '#43a047' },
        expired: { label: 'Expired', icon: 'event_busy', color: '#9e9e9e' },
        cancelled: { label: 'Cancelled', icon: 'cancel', color: '#ef5350' },
        revoked: { label: 'Revoked', icon: 'block', color: '#ef5350' },
        archived: { label: 'Archived', icon: 'archive', color: '#78909c' },
        deleted: { label: 'Deleted', icon: 'delete', color: '#ef5350' },
    };

    private statusOrder = [
        'published',
        'approved',
        'scheduled',
        'pending_approval',
        'draft',
        'expired',
        'archived',
        'cancelled',
        'revoked',
        'deleted',
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.computeStatus();
        }
    }

    public getStatusCfg(key: string): { icon: string; color: string; label: string } {
        return this.statusConfig[key] || this.statusConfig['draft'];
    }

    public isStatusActive(statusKey: string): boolean {
        return this.activeStatusFilters.has(statusKey);
    }

    public toggleStatusFilter(statusKey: string): void {
        if (this.activeStatusFilters.has(statusKey)) {
            this.activeStatusFilters.delete(statusKey);
        } else {
            this.activeStatusFilters.add(statusKey);
        }
        this.activeFilterKeys = Array.from(this.activeStatusFilters);
        this.applyFilters();
    }

    public clearStatusFilters(): void {
        this.activeStatusFilters.clear();
        this.activeFilterKeys = [];
        this.applyFilters();
    }

    public clearFilters(): void {
        this.searchTerm = '';
        this.activeStatusFilters.clear();
        this.activeFilterKeys = [];
        this.applyFilters();
    }

    public isMatch(item: PolicyItem): boolean {
        if (!this.searchTerm) return false;
        return this.fuzzyMatch(item.searchText, this.searchTerm.toLowerCase());
    }

    public applyFilters(): void {
        let items = this.policyItems;

        // Status filters (multi-select: show items matching ANY active filter)
        if (this.activeStatusFilters.size > 0) {
            items = items.filter((p) => this.activeStatusFilters.has(p.statusKey));
        }

        // Fuzzy search
        if (this.searchTerm) {
            const term = this.searchTerm.toLowerCase();
            items = items.filter((p) => this.fuzzyMatch(p.searchText, term));
        }

        this.filteredItems = items;
    }

    /**
     * Fuzzy match: checks if all characters in the needle appear in order
     * within the haystack. Falls back to substring match if fuzzy fails.
     */
    private fuzzyMatch(haystack: string, needle: string): boolean {
        // Exact substring first
        if (haystack.includes(needle)) return true;

        // Split into words and check all words match somewhere
        const words = needle.split(/\s+/).filter((w) => w.length > 0);
        if (words.length > 1) {
            return words.every((w) => haystack.includes(w));
        }

        // Character-by-character fuzzy
        let hi = 0;
        for (let ni = 0; ni < needle.length; ni++) {
            const ch = needle[ni];
            while (hi < haystack.length && haystack[hi] !== ch) hi++;
            if (hi >= haystack.length) return false;
            hi++;
        }
        return true;
    }

    private formatTimestamp(val: unknown): string {
        if (!val) return '';
        try {
            // Firestore Timestamp with toDate()
            if (typeof val === 'object' && val !== null && 'toDate' in val) {
                const d = (val as { toDate: () => Date }).toDate();
                return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            // Firestore Timestamp with seconds (emulator format)
            if (typeof val === 'object' && val !== null && 'seconds' in val) {
                const secs = (val as { seconds: number }).seconds;
                const d = new Date(secs * 1000);
                return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            // Firestore Timestamp with _seconds (serialized format)
            if (typeof val === 'object' && val !== null && '_seconds' in val) {
                const secs = (val as { _seconds: number })._seconds;
                const d = new Date(secs * 1000);
                return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            // Already a Date or ISO string
            const d = new Date(val as string | number);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
        } catch {
            /* ignore */
        }
        return '';
    }

    private computeStatus(): void {
        const policies = this.data.policies || [];
        this.totalPolicies = policies.length;

        const statusMap: Record<string, number> = {};
        this.policyItems = [];

        for (const policy of policies) {
            const d = policy.data as Record<string, unknown>;
            if (!d) continue;

            let status = ((d['status'] as string) || '').toLowerCase();
            if (!status || !this.statusConfig[status]) {
                if (d['isPublished']) status = 'published';
                else if (d['isArchived']) status = 'archived';
                else status = 'draft';
            }

            statusMap[status] = (statusMap[status] || 0) + 1;

            const cfg = this.statusConfig[status] || this.statusConfig['draft'];
            const name = (d['name'] as string) || policy.key;
            const category = (d['category'] as string) || '';
            const customCategory = (d['customCategory'] as string) || '';
            const description = (d['description'] as string) || '';
            const version = (d['version'] as number) || 0;
            const effectiveDate = this.formatTimestamp(d['effectiveDate']);
            const publishDate = this.formatTimestamp(d['publishDate']);
            const expiryDate = this.formatTimestamp(d['expiryDate']);

            // Build searchable text: name, category, status, description, dates
            const searchText = [
                name,
                category,
                customCategory,
                cfg.label,
                description,
                effectiveDate,
                publishDate,
                expiryDate,
                version > 0 ? `v${version}` : '',
            ]
                .join(' ')
                .toLowerCase();

            this.policyItems.push({
                key: policy.key,
                name,
                category: customCategory || category,
                status,
                statusKey: status,
                statusIcon: cfg.icon,
                statusColor: cfg.color,
                statusLabel: cfg.label,
                description: description.length > 80 ? description.substring(0, 80) + '...' : description,
                version,
                effectiveDate,
                publishDate,
                expiryDate,
                searchText,
            });
        }

        // Build status groups
        this.statusGroups = this.statusOrder
            .filter((s) => statusMap[s])
            .map((status) => {
                const cfg = this.statusConfig[status] || this.statusConfig['draft'];
                return {
                    statusKey: status,
                    status: cfg.label,
                    count: statusMap[status],
                    icon: cfg.icon,
                    color: cfg.color,
                };
            });

        for (const [status, count] of Object.entries(statusMap)) {
            if (!this.statusOrder.includes(status)) {
                const cfg = this.statusConfig[status] || this.statusConfig['draft'];
                this.statusGroups.push({
                    statusKey: status,
                    status: cfg.label,
                    count,
                    icon: cfg.icon,
                    color: cfg.color,
                });
            }
        }

        this.applyFilters();
    }

    public onPolicyClick(p: PolicyItem): void {
        const policyEntity = (this.data.policies || []).find((pol) => pol.key === p.key);
        if (policyEntity) {
            this.dialogService.openPolicyDetailsDialog(p.key, policyEntity.data);
        }
    }
}
